import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import regionConfig from '../../config/regionConfig';
import { SidebarContext } from "../../AppRoutes";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from '../../components/PhoneInput';
import { toE164, fromE164 } from '../../utils/phone';

import { getEntregadores, registerEntregador, updateEntregador, deleteEntregador } from "../../services/api";
import PermissionGate from "../../services/PermissionGate";
import ConfirmDialog from "../../components/ConfirmDialog";

import { FiUserPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiX, FiUser, FiPhone } from 'react-icons/fi';
import { IoCarOutline } from "react-icons/io5";
import '../../styles/page-titles.css';
import { BiCar } from 'react-icons/bi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

// ==============================
// STYLED COMPONENTS
// ==============================
const PageContainer = styled.div`
    display: flex;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background: white;
    overflow: initial;
    z-index: 9;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const ContentWrapper = styled.div`
    width: 100%;
    margin: 0 auto;
`;

const PageHeader = styled.div`
    margin-bottom: 24px;

    .entregadores-page-subtitle {
        color: #718096;
        font-size: 14px;
        margin: 4px 0 0 0;
    }
`;

const ControlsBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 16px;
    flex-wrap: wrap;

    .entregadores-search-box {
        position: relative;
        flex: 1;
        max-width: 400px;
        min-width: 200px;
    }

    .entregadores-search-box input {
        width: 100%;
        padding: 10px 14px 10px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        font-size: 14px;
        background: #fff;
        transition: all 0.2s ease;
        outline: none;
    }

    .entregadores-search-box input:focus {
        border-color: rgb(49, 140, 213);
        box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.15);
    }

    .entregadores-search-box .entregadores-search-icon {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #a0aec0;
        font-size: 16px;
    }

    .entregadores-controls-right {
        display: flex;
        gap: 10px;
        align-items: center;
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;

        .entregadores-search-box {
            max-width: 100%;
        }

        .entregadores-controls-right {
            justify-content: stretch;
        }
    }
`;

const RefreshButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    color: #4a5568;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: rgb(49, 140, 213);
        color: rgb(49, 140, 213);
        background: rgba(49, 140, 213, 0.04);
    }

    svg {
        font-size: 16px;
    }
`;

const AddButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(49, 140, 213, 0.3);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(49, 140, 213, 0.4);
    }

    &:active {
        transform: translateY(0);
    }

    svg {
        font-size: 18px;
    }
`;

const EntregadoresCard = styled.div`
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
`;

const EntregadoresTable = styled.div`
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    table {
        width: 100%;
        border-collapse: collapse;
        min-width: 750px;
    }

    thead {
        background: #f7fafc;
        border-bottom: 2px solid #edf2f7;
    }

    thead th {
        padding: 14px 20px;
        font-size: 12px;
        font-weight: 600;
        color: #718096;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: left;
        white-space: nowrap;
    }

    tbody tr {
        border-bottom: 1px solid #edf2f7;
        transition: background 0.15s ease;
    }

    tbody tr:hover {
        background: #f7fafc;
    }

    tbody tr:last-child {
        border-bottom: none;
    }

    tbody td {
        padding: 16px 20px;
        font-size: 14px;
        color: #2d3748;
        vertical-align: middle;
    }

    .entregadores-member-info {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .entregadores-member-avatar {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        color: #fff;
        text-transform: uppercase;
        flex-shrink: 0;
        background: rgba(49, 140, 213, 0.12);
    }

    .entregadores-member-name {
        font-weight: 600;
        color: #1a202c;
        line-height: 1.3;
    }

    .entregadores-member-phone {
        font-size: 13px;
        color: #a0aec0;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .entregadores-member-phone svg {
        font-size: 12px;
    }

    .entregadores-vehicle-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
    }

    .entregadores-vehicle-badge svg {
        font-size: 12px;
    }

    .entregadores-date-cell {
        color: #718096;
        font-size: 13px;
        white-space: nowrap;
    }

    .entregadores-plate-cell {
        color: #4a5568;
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.5px;
    }

    .entregadores-actions-cell {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .entregadores-action-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        background: #fff;
        cursor: pointer;
        transition: all 0.2s ease;
        color: #718096;
    }

    .entregadores-action-btn:hover {
        border-color: rgb(49, 140, 213);
        color: rgb(49, 140, 213);
        background: rgba(49, 140, 213, 0.04);
    }

    .entregadores-action-btn.entregadores-action-danger:hover {
        border-color: #e53e3e;
        color: #e53e3e;
        background: rgba(229, 62, 62, 0.04);
    }

    .entregadores-action-btn svg {
        font-size: 16px;
    }

    @media (max-width: 600px) {
        table {
            min-width: 600px;
        }
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    text-align: center;

    .entregadores-empty-icon {
        font-size: 56px;
        color: #cbd5e0;
        margin-bottom: 16px;
    }

    .entregadores-empty-text {
        font-size: 16px;
        color: #718096;
        max-width: 360px;
        line-height: 1.6;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;

    svg {
        font-size: 48px;
        color: rgb(49, 140, 213);
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

// ==============================
// MODAL STYLED COMPONENTS
// ==============================
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 24px;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ModalContent = styled.div`
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 560px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    animation: slideUp 0.3s ease;
    max-height: 90vh;
    overflow-y: auto;

    @keyframes slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
    }

    .entregadores-modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 24px;
        border-bottom: 1px solid #edf2f7;
    }

    .entregadores-modal-header h2 {
        font-size: 20px;
        font-weight: 700;
        color: #1a202c;
        margin: 0;
    }

    .entregadores-modal-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 8px;
        background: #f7fafc;
        cursor: pointer;
        color: #718096;
        transition: all 0.2s ease;
    }

    .entregadores-modal-close:hover {
        background: #edf2f7;
        color: #1a202c;
    }

    .entregadores-modal-body {
        padding: 24px;
    }

    .entregadores-modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 16px 24px;
        border-top: 1px solid #edf2f7;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 18px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: #4a5568;
        margin-bottom: 6px;
    }

    input, select {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        font-size: 14px;
        color: #1a202c;
        outline: none;
        transition: all 0.2s ease;
        background: #fff;
    }

    input:focus, select:focus {
        border-color: rgb(49, 140, 213);
        box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.15);
    }

    input::placeholder {
        color: #cbd5e0;
    }

    select {
        appearance: none;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
        background-position: right 12px center;
        background-repeat: no-repeat;
        background-size: 16px 16px;
        padding-right: 36px;
    }

    .entregadores-form-error {
        font-size: 12px;
        color: #e53e3e;
        margin-top: 4px;
    }
`;

const ModalCancelButton = styled.button`
    padding: 10px 20px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    color: #4a5568;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #f7fafc;
        border-color: #cbd5e0;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ModalSubmitButton = styled.button`
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(49, 140, 213, 0.3);
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(49, 140, 213, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }

    .entregadores-btn-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }
`;

// ==============================
// HELPERS
// ==============================
const getVehicleColor = (vehicle) => {
    switch (vehicle) {
        case 'Motocicleta': return '#f59e0b';
        case 'Carro': return '#22c55e';
        case 'Bicicleta': return '#a855f7';
        default: return '#718096';
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleDateString(i18n.language || regionConfig.locale.default, {
            day: '2-digit', month: '2-digit', year: 'numeric',
        });
    } catch {
        return '-';
    }
};

// ==============================
// COMPONENT
// ==============================
const ListEntregadores = () => {
    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('entregadores');

    // Data state
    const [entregadores, setEntregadores] = useState([]);
    const [filter, setFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [refreshFlag, setRefreshFlag] = useState(false);

    // Add modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [addForm, setAddForm] = useState({ nome: '', telefone: '', veiculo: '', placa: '' });
    const [addErrors, setAddErrors] = useState({});

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editForm, setEditForm] = useState({ _id: '', nome: '', telefone: '', veiculo: '', placa: '' });
    const [editErrors, setEditErrors] = useState({});

    // Delete state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [entregadorToDelete, setEntregadorToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ==============================
    // FETCH
    // ==============================
    const fetchEntregadores = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getEntregadores();
            setEntregadores(response.data.entregadores || []);
        } catch (error) {
            console.error('[ENTREGADORES] Erro ao carregar:', error);
            toast.error(t('fetchError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchEntregadores();
    }, [fetchEntregadores, refreshFlag]);

    // ==============================
    // FILTER
    // ==============================
    const filteredEntregadores = useMemo(() => {
        if (!filter) return entregadores;
        const exp = new RegExp(filter.replace(/[^\d\w]+/g, '.*'), 'i');
        return entregadores.filter(item =>
            exp.test(item.name) ||
            exp.test(item.telefone) ||
            exp.test(item.veiculo) ||
            exp.test(item.placa)
        );
    }, [filter, entregadores]);

    // ==============================
    // ADD MODAL
    // ==============================
    const openAddModal = () => {
        setAddForm({ nome: '', telefone: '', veiculo: '', placa: '' });
        setAddErrors({});
        setShowAddModal(true);
    };

    const validateAddForm = () => {
        const errors = {};
        if (!addForm.nome.trim()) errors.nome = true;
        if (!addForm.telefone) errors.telefone = true;
        if (!addForm.veiculo) errors.veiculo = true;
        setAddErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddSubmit = async () => {
        if (!validateAddForm()) return;
        setAddLoading(true);
        try {
            await registerEntregador(
                addForm.nome.trim(),
                fromE164(addForm.telefone),
                addForm.veiculo,
                addForm.placa.trim()
            );
            toast.success(t('addModal.success'));
            setShowAddModal(false);
            setRefreshFlag(prev => !prev);
        } catch (error) {
            const msg = error?.response?.data?.msg || t('addModal.error');
            toast.error(msg);
        } finally {
            setAddLoading(false);
        }
    };

    // ==============================
    // EDIT MODAL
    // ==============================
    const openEditModal = (entregador) => {
        setEditForm({
            _id: entregador._id,
            nome: entregador.name,
            telefone: toE164(entregador.telefone_completo || entregador.telefone) || '',
            veiculo: entregador.veiculo,
            placa: entregador.placa || ''
        });
        setEditErrors({});
        setShowEditModal(true);
    };

    const validateEditForm = () => {
        const errors = {};
        if (!editForm.nome.trim()) errors.nome = true;
        if (!editForm.telefone) errors.telefone = true;
        if (!editForm.veiculo) errors.veiculo = true;
        setEditErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleEditSubmit = async () => {
        if (!validateEditForm()) return;
        setEditLoading(true);
        try {
            await updateEntregador(
                editForm._id,
                editForm.nome.trim(),
                editForm.veiculo,
                fromE164(editForm.telefone),
                editForm.placa.trim()
            );
            toast.success(t('editModal.success'));
            setShowEditModal(false);
            setRefreshFlag(prev => !prev);
        } catch (error) {
            const msg = error?.response?.data?.msg || t('editModal.error');
            toast.error(msg);
        } finally {
            setEditLoading(false);
        }
    };

    // ==============================
    // DELETE
    // ==============================
    const openDeleteConfirm = (entregador) => {
        setEntregadorToDelete(entregador);
        setConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!entregadorToDelete) return;
        setDeleteLoading(true);
        try {
            await deleteEntregador(entregadorToDelete._id);
            toast.success(t('actions.deleteSuccess'));
            setConfirmOpen(false);
            setEntregadorToDelete(null);
            setRefreshFlag(prev => !prev);
        } catch (error) {
            const msg = error?.response?.data?.msg || t('actions.deleteError');
            toast.error(msg);
        } finally {
            setDeleteLoading(false);
        }
    };

    // ==============================
    // RENDER
    // ==============================
    return (
        <PermissionGate permissions={['default']}>
            <PageContainer sidebar={sidebar}>
                <ContentWrapper>
                    {/* Header */}
                    <PageHeader>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><IoCarOutline /></div>
                            <h1 className="page-title">{t('pageTitle')}</h1>
                        </div>
                        <p className="entregadores-page-subtitle">{t('subtitle')}</p>
                    </PageHeader>

                    {/* Controls */}
                    <ControlsBar>
                        <div className="entregadores-search-box">
                            <FiSearch className="entregadores-search-icon" />
                            <input
                                type="text"
                                placeholder={t('searchPlaceholder')}
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                        </div>

                        <div className="entregadores-controls-right">
                            <RefreshButton onClick={() => { setLoading(true); setRefreshFlag(prev => !prev); }}>
                                <FiRefreshCw /> {t('refreshButton')}
                            </RefreshButton>

                            <AddButton onClick={openAddModal}>
                                <FiUserPlus /> {t('addButton')}
                            </AddButton>
                        </div>
                    </ControlsBar>

                    {/* Table Card */}
                    <EntregadoresCard>
                        {loading ? (
                            <LoadingContainer>
                                <AiOutlineLoading3Quarters />
                            </LoadingContainer>
                        ) : filteredEntregadores.length === 0 ? (
                            <EmptyState>
                                <IoCarOutline className="entregadores-empty-icon" />
                                <p className="entregadores-empty-text">
                                    {filter
                                        ? t('emptyState').split('.')[0] + '.'
                                        : t('emptyState')
                                    }
                                </p>
                            </EmptyState>
                        ) : (
                            <EntregadoresTable>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t('table.name')}</th>
                                            <th>{t('table.vehicle')}</th>
                                            <th>{t('table.plate')}</th>
                                            <th>{t('table.createdAt')}</th>
                                            <th>{t('table.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEntregadores.map((entregador) => {
                                            const vehicleColor = getVehicleColor(entregador.veiculo);

                                            return (
                                                <tr key={entregador._id}>
                                                    {/* Name + Phone */}
                                                    <td>
                                                        <div className="entregadores-member-info">
                                                            <div className="entregadores-member-avatar">
                                                                <FiUser style={{ fontSize: 18, color: 'rgb(49, 140, 213)' }} />
                                                            </div>
                                                            <div>
                                                                <div className="entregadores-member-name">
                                                                    {entregador.name}
                                                                </div>
                                                                <div className="entregadores-member-phone">
                                                                    <FiPhone /> {entregador.telefone}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Vehicle */}
                                                    <td>
                                                        <span
                                                            className="entregadores-vehicle-badge"
                                                            style={{
                                                                background: `${vehicleColor}15`,
                                                                color: vehicleColor,
                                                            }}
                                                        >
                                                            <BiCar /> {t(`vehicles.${entregador.veiculo}`, entregador.veiculo)}
                                                        </span>
                                                    </td>

                                                    {/* Plate */}
                                                    <td>
                                                        <span className="entregadores-plate-cell">
                                                            {entregador.placa || '-'}
                                                        </span>
                                                    </td>

                                                    {/* Created At */}
                                                    <td>
                                                        <span className="entregadores-date-cell">
                                                            {formatDate(entregador.createdAt)}
                                                        </span>
                                                    </td>

                                                    {/* Actions */}
                                                    <td>
                                                        <div className="entregadores-actions-cell">
                                                            <button
                                                                className="entregadores-action-btn"
                                                                title={t('actions.edit')}
                                                                onClick={() => openEditModal(entregador)}
                                                            >
                                                                <FiEdit2 />
                                                            </button>
                                                            <button
                                                                className="entregadores-action-btn entregadores-action-danger"
                                                                title={t('actions.delete')}
                                                                onClick={() => openDeleteConfirm(entregador)}
                                                            >
                                                                <FiTrash2 />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </EntregadoresTable>
                        )}
                    </EntregadoresCard>
                </ContentWrapper>
            </PageContainer>

            {/* ========== ADD MODAL ========== */}
            {showAddModal && (
                <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
                    <ModalContent>
                        <div className="entregadores-modal-header">
                            <h2>{t('addModal.title')}</h2>
                            <button className="entregadores-modal-close" onClick={() => setShowAddModal(false)}>
                                <FiX />
                            </button>
                        </div>

                        <div className="entregadores-modal-body">
                            <FormGroup>
                                <label>{t('addModal.name')} <span style={{ color: '#e53e3e' }}>*</span></label>
                                <input
                                    type="text"
                                    placeholder={t('addModal.namePlaceholder')}
                                    value={addForm.nome}
                                    onChange={(e) => setAddForm(prev => ({ ...prev, nome: e.target.value }))}
                                    style={addErrors.nome ? { borderColor: '#e53e3e' } : {}}
                                    autoFocus
                                />
                                {addErrors.nome && <div className="entregadores-form-error">{t('validation.nameRequired')}</div>}
                            </FormGroup>

                            <FormGroup>
                                <label>{t('addModal.vehicle')} <span style={{ color: '#e53e3e' }}>*</span></label>
                                <select
                                    value={addForm.veiculo}
                                    onChange={(e) => setAddForm(prev => ({ ...prev, veiculo: e.target.value }))}
                                    style={addErrors.veiculo ? { borderColor: '#e53e3e' } : {}}
                                >
                                    <option value="">{t('addModal.vehiclePlaceholder')}</option>
                                    <option value="Bicicleta">{t('vehicles.Bicicleta')}</option>
                                    <option value="Carro">{t('vehicles.Carro')}</option>
                                    <option value="Motocicleta">{t('vehicles.Motocicleta')}</option>
                                </select>
                                {addErrors.veiculo && <div className="entregadores-form-error">{t('validation.vehicleRequired')}</div>}
                            </FormGroup>

                            <FormGroup>
                                <label>{t('addModal.phone')} <span style={{ color: '#e53e3e' }}>*</span></label>
                                <PhoneInput
                                    value={addForm.telefone}
                                    onChange={(val) => setAddForm(prev => ({ ...prev, telefone: val }))}
                                    placeholder={t('addModal.phonePlaceholder')}
                                    style={addErrors.telefone ? { borderColor: '#e53e3e', borderRadius: '8px' } : {}}
                                />
                                {addErrors.telefone && <div className="entregadores-form-error">{t('validation.phoneRequired')}</div>}
                            </FormGroup>

                            <FormGroup>
                                <label>{t('addModal.plate')}</label>
                                <input
                                    type="text"
                                    placeholder={t('addModal.platePlaceholder')}
                                    value={addForm.placa}
                                    onChange={(e) => setAddForm(prev => ({ ...prev, placa: e.target.value }))}
                                />
                            </FormGroup>
                        </div>

                        <div className="entregadores-modal-footer">
                            <ModalCancelButton onClick={() => setShowAddModal(false)} disabled={addLoading}>
                                {t('addModal.cancel')}
                            </ModalCancelButton>
                            <ModalSubmitButton onClick={handleAddSubmit} disabled={addLoading}>
                                {addLoading && <div className="entregadores-btn-spinner" />}
                                {t('addModal.submit')}
                            </ModalSubmitButton>
                        </div>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* ========== EDIT MODAL ========== */}
            {showEditModal && (
                <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) setShowEditModal(false); }}>
                    <ModalContent>
                        <div className="entregadores-modal-header">
                            <h2>{t('editModal.title')}</h2>
                            <button className="entregadores-modal-close" onClick={() => setShowEditModal(false)}>
                                <FiX />
                            </button>
                        </div>

                        <div className="entregadores-modal-body">
                            <FormGroup>
                                <label>{t('addModal.name')} <span style={{ color: '#e53e3e' }}>*</span></label>
                                <input
                                    type="text"
                                    placeholder={t('addModal.namePlaceholder')}
                                    value={editForm.nome}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, nome: e.target.value }))}
                                    style={editErrors.nome ? { borderColor: '#e53e3e' } : {}}
                                    autoFocus
                                />
                                {editErrors.nome && <div className="entregadores-form-error">{t('validation.nameRequired')}</div>}
                            </FormGroup>

                            <FormGroup>
                                <label>{t('addModal.vehicle')} <span style={{ color: '#e53e3e' }}>*</span></label>
                                <select
                                    value={editForm.veiculo}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, veiculo: e.target.value }))}
                                    style={editErrors.veiculo ? { borderColor: '#e53e3e' } : {}}
                                >
                                    <option value="">{t('addModal.vehiclePlaceholder')}</option>
                                    <option value="Bicicleta">{t('vehicles.Bicicleta')}</option>
                                    <option value="Carro">{t('vehicles.Carro')}</option>
                                    <option value="Motocicleta">{t('vehicles.Motocicleta')}</option>
                                </select>
                                {editErrors.veiculo && <div className="entregadores-form-error">{t('validation.vehicleRequired')}</div>}
                            </FormGroup>

                            <FormGroup>
                                <label>{t('addModal.phone')} <span style={{ color: '#e53e3e' }}>*</span></label>
                                <PhoneInput
                                    value={editForm.telefone}
                                    onChange={(val) => setEditForm(prev => ({ ...prev, telefone: val }))}
                                    placeholder={t('addModal.phonePlaceholder')}
                                    style={editErrors.telefone ? { borderColor: '#e53e3e', borderRadius: '8px' } : {}}
                                />
                                {editErrors.telefone && <div className="entregadores-form-error">{t('validation.phoneRequired')}</div>}
                            </FormGroup>

                            <FormGroup>
                                <label>{t('addModal.plate')}</label>
                                <input
                                    type="text"
                                    placeholder={t('addModal.platePlaceholder')}
                                    value={editForm.placa}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, placa: e.target.value }))}
                                />
                            </FormGroup>
                        </div>

                        <div className="entregadores-modal-footer">
                            <ModalCancelButton onClick={() => setShowEditModal(false)} disabled={editLoading}>
                                {t('editModal.cancel')}
                            </ModalCancelButton>
                            <ModalSubmitButton onClick={handleEditSubmit} disabled={editLoading}>
                                {editLoading && <div className="entregadores-btn-spinner" />}
                                {t('editModal.submit')}
                            </ModalSubmitButton>
                        </div>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* ========== DELETE CONFIRM ========== */}
            <ConfirmDialog
                title={t('actions.confirmDeleteTitle')}
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
            >
                {entregadorToDelete
                    ? t('actions.confirmDeleteMessage', { name: entregadorToDelete.name })
                    : ''
                }
            </ConfirmDialog>
        </PermissionGate>
    );
};

export default ListEntregadores;
